// Extra CMS slots for the 'dispute_resolution' page — added to make previously-hardcoded
// public marketing copy editable. Concatenated into CMS_SLOTS via ./index.
// Each default MUST equal the exact copy the component rendered before wiring,
// so the public page is byte-identical until an admin edits it.
import type { SlotDef } from '../slots';

export const disputeResolutionExtra: SlotDef[] = [
  // ─── Categories section header ────────────────────────────────────────
  {
    key: 'dispute_resolution.categories_eyebrow',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Categories: eyebrow',
    default: 'Categories',
  },
  {
    key: 'dispute_resolution.categories_title',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Categories: title',
    default: 'What counts as a concern.',
  },
  {
    key: 'dispute_resolution.categories_lede',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Categories: lede',
    default:
      'Anything that meaningfully affects the exchange or the safety of either party. The categories below cover what we hear in practice.',
  },

  // ─── Process section header ───────────────────────────────────────────
  {
    key: 'dispute_resolution.process_eyebrow',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Process: eyebrow',
    default: 'The process',
  },
  {
    key: 'dispute_resolution.process_title',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Process: title',
    default: 'How it works.',
  },
  {
    key: 'dispute_resolution.process_lede',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Process: lede',
    default: 'Five steps. We tell you where we are at each one. No algorithmic limbo.',
  },

  // ─── Principles section header ────────────────────────────────────────
  {
    key: 'dispute_resolution.principles_eyebrow',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Principles: eyebrow',
    default: 'The principles',
  },
  {
    key: 'dispute_resolution.principles_title',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Principles: title',
    default: 'How we work.',
  },

  // ─── "Open a concern" CTA section ─────────────────────────────────────
  {
    key: 'dispute_resolution.cta_eyebrow',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Open a concern: eyebrow',
    default: 'Get in touch',
  },
  {
    key: 'dispute_resolution.cta_title',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Open a concern: title',
    default: 'Open a concern.',
  },
  {
    key: 'dispute_resolution.cta_body',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Open a concern: body',
    default:
      'You need to be signed in to open a concern, and the exchange needs to be at least confirmed. From the exchange thread, tap "Raise a concern."',
  },
  {
    key: 'dispute_resolution.cta_button',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Open a concern: button label',
    helpText: 'Button text only — it always links to the sign-in-gated concern flow.',
    default: 'Open a concern (sign in required)',
  },
  {
    key: 'dispute_resolution.cta_contact_prefix',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Open a concern: contact-line text',
    helpText: 'The sentence before the contact email; a trailing full stop is added after the email.',
    default: "For anything that isn't exchange-specific, write to us at",
  },
  {
    key: 'dispute_resolution.cta_contact_email',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Open a concern: contact email',
    helpText: 'Shown as a clickable mailto link.',
    default: 'hello@wwaave.com',
  },

  // ─── "The wider picture" next-steps tiles ─────────────────────────────
  {
    key: 'dispute_resolution.wider_title',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Wider picture: title',
    default: 'The wider picture.',
  },
  {
    key: 'dispute_resolution.wider_lede',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Wider picture: lede',
    default:
      'The promise explains why we run disputes the way we do. The terms are the formal version.',
  },
  {
    key: 'dispute_resolution.wider_tile1_title',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Wider picture: tile 1 title',
    helpText: 'Links to the WWAAVE Promise page (route fixed).',
    default: 'The WWAAVE Promise',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'dispute_resolution.wider_tile1_desc',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Wider picture: tile 1 description',
    default: 'The principles that shape every decision we make.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'dispute_resolution.wider_tile2_title',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Wider picture: tile 2 title',
    helpText: 'Links to the terms page (route fixed).',
    default: 'Terms of service',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'dispute_resolution.wider_tile2_desc',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Wider picture: tile 2 description',
    default: 'The legal frame around exchanges, accounts, and disputes.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'dispute_resolution.wider_tile3_title',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Wider picture: tile 3 title',
    helpText: 'Links to the FAQ page (route fixed).',
    default: 'FAQ',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'dispute_resolution.wider_tile3_desc',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Wider picture: tile 3 description',
    default: 'Quick answers to the five questions we hear most.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
];
